import {
  useDisclosure,
  useColorModeValue,
  Popover,
  PopoverAnchor,
  Button,
  Portal,
  Tabs,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  PopoverBody,
  SimpleGrid,
  Center,
  Box,
  PopoverTrigger,
  Input,
} from '@chakra-ui/react';
import { CellContext } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import { useFormContext } from 'react-hook-form';
import { HexColorPicker } from 'react-colorful';
import {
  EditableRowsFormValues,
  EditableRowFormValues,
} from '../project/order-entries-panel/OrderEntriesPanel';
import { TableEntry, isEditableTableMeta, isEditingRow } from './Table';
import { getColorValueFromGradient } from 'src/utils/color-util';
import { COLORS, GRADIENTS } from 'src/utils/row-color-util';

export function ColorPicker<D extends TableEntry>({
  getValue,
  column,
  row,
  table: {
    options: { meta },
  },
}: CellContext<D, string>): React.ReactElement {
  const { setValue, watch } = useFormContext<EditableRowsFormValues>();
  const columnId = column.id as keyof EditableRowFormValues;
  const currentColor = watch(`${row.id}.${columnId}`) as string;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isCustomColorOpen,
    onOpen: onCustomColorOpen,
    onClose: onCustomColorClose,
  } = useDisclosure();
  const editingOtherColor = useColorModeValue('gray.200', 'gray.600');
  invariant(isEditableTableMeta(meta), 'Table meta is not defined');

  const { t } = useTranslation('table');

  if (isEditingRow(row, meta)) {
    return (
      <Popover
        isLazy
        isOpen={isOpen}
        onClose={() => {
          onClose();
          onCustomColorClose();
        }}
      >
        <PopoverAnchor>
          <Button
            aria-label="color"
            variant="unstyled"
            _hover={{ opacity: '0.6' }}
            height="16px"
            width="16px"
            padding={0}
            minWidth="unset"
            borderRadius="full"
            background={currentColor}
            onClick={onOpen}
          />
        </PopoverAnchor>
        <Portal>
          <Tabs
            variant="panel-tabs"
            isLazy
            size={'sm'}
            defaultIndex={currentColor.includes('gradient') ? 1 : 0}
          >
            <PopoverContent maxWidth="180">
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverHeader>
                <TabList>
                  <Tab>{t('color-picker.tab.color')}</Tab>
                  <Tab>{t('color-picker.tab.gradient')}</Tab>
                </TabList>
              </PopoverHeader>
              <TabPanels>
                <TabPanel>
                  <PopoverBody>
                    <SimpleGrid alignItems="center" columns={4} spacing={2}>
                      {COLORS.map((c) => (
                        <Center key={c}>
                          <Button
                            aria-label={c}
                            background={c}
                            height={6}
                            width={6}
                            padding={0}
                            minWidth="unset"
                            borderRadius="full"
                            outline={
                              currentColor === c ? `2px solid ${c}` : undefined
                            }
                            outlineOffset={3}
                            _hover={{ background: c }}
                            onClick={() => {
                              setValue(`${row.id}.${columnId}`, c);
                              onClose();
                            }}
                          />
                        </Center>
                      ))}
                      <Popover
                        isLazy
                        isOpen={isCustomColorOpen}
                        onClose={onCustomColorClose}
                      >
                        <PopoverTrigger>
                          <Input
                            width="156px"
                            variant="filled"
                            value={currentColor}
                            onChange={(e) => {
                              setValue(`${row.id}.${columnId}`, e.target.value);
                            }}
                            size="xs"
                            borderRadius={'md'}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!isCustomColorOpen) {
                                onCustomColorOpen();
                                // Keep focus on input
                                e.currentTarget.focus();
                              }
                            }}
                            onFocus={(e) => e.target.select()}
                          />
                        </PopoverTrigger>
                        <PopoverContent
                          width="220px"
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          <PopoverBody padding={4}>
                            <HexColorPicker
                              color={currentColor}
                              onChange={(color) => {
                                setValue(`${row.id}.${columnId}`, color);
                              }}
                            />
                          </PopoverBody>
                        </PopoverContent>
                      </Popover>
                    </SimpleGrid>
                  </PopoverBody>
                </TabPanel>
                <TabPanel>
                  <PopoverBody>
                    <SimpleGrid alignItems="center" columns={4} spacing={2}>
                      {GRADIENTS.map((gradient) => (
                        <Center key={gradient}>
                          <Button
                            background={gradient}
                            _hover={{
                              background: gradient,
                            }}
                            aria-label={gradient}
                            height={6}
                            width={6}
                            padding={0}
                            minWidth="unset"
                            borderRadius="full"
                            outline={
                              currentColor === gradient
                                ? `2px solid ${getColorValueFromGradient(
                                    gradient,
                                    0.5
                                  )}`
                                : undefined
                            }
                            outlineOffset={3}
                            onClick={() => {
                              setValue(`${row.id}.${columnId}`, gradient);
                              onClose();
                            }}
                          />
                        </Center>
                      ))}
                    </SimpleGrid>
                  </PopoverBody>
                </TabPanel>
              </TabPanels>
            </PopoverContent>
          </Tabs>
        </Portal>
      </Popover>
    );
  } else {
    const isEditingOtherRow = meta.editingRows.some(
      (editingRow) => editingRow !== row.id
    );
    return (
      <Box
        height={row.depth === 0 ? 4 : 2.5}
        width={row.depth === 0 ? 4 : 2.5}
        marginLeft={row.depth === 0 ? 0 : '3px'}
        padding={0}
        minWidth="unset"
        borderRadius="full"
        background={isEditingOtherRow ? editingOtherColor : getValue()}
      />
    );
  }
}
